import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, PhoneIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
export function RegisterPage() {
  const navigate = useNavigate();
  const {
    register
  } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.password) newErrors.password = 'Password is required';else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const success = await register(formData.name, formData.email, formData.password);
      if (success) {
        navigate('/account');
      }
    } catch {
      setErrors({
        form: 'An error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-id="element-1573">
      <div className="w-full max-w-md" data-id="element-1574">
        {/* Logo */}
        <div className="text-center mb-8" data-id="element-1575">
          <Link to="/" className="inline-flex items-center gap-2" data-id="element-1576">
            <img src="https://cdn.magicpatterns.com/uploads/fRsBpVfPoCHsQawYgHjccg/ChatGPT_Image_Mar_6,_2026,_03_48_02_AM.png" alt="Gadgets Zone" className="h-12 w-auto object-contain" data-id="element-1577" />
            <span className="text-2xl font-bold text-gray-900" data-id="element-1578">
              Gadgets Zone
            </span>
          </Link>
        </div>

        <Card padding="lg" data-id="element-1579">
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" data-id="element-1580">
            Create Account
          </h1>
          <p className="text-gray-600 text-center mb-6" data-id="element-1581">
            Join Gadgets Zone for exclusive deals
          </p>

          {errors.form && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-id="element-1582">
              {errors.form}
            </div>}

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1583">
            <Input label="Full Name" placeholder="Enter your full name" value={formData.name} onChange={e => handleChange('name', e.target.value)} leftIcon={<UserIcon className="w-5 h-5" data-id="element-1585" />} error={errors.name} required data-id="element-1584" />

            <Input label="Email Address" type="email" placeholder="your@email.com" value={formData.email} onChange={e => handleChange('email', e.target.value)} leftIcon={<MailIcon className="w-5 h-5" data-id="element-1587" />} error={errors.email} required data-id="element-1586" />

            <Input label="Phone Number" type="tel" placeholder="+92-XXX-XXXXXXX" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} leftIcon={<PhoneIcon className="w-5 h-5" data-id="element-1589" />} error={errors.phone} required data-id="element-1588" />

            <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={formData.password} onChange={e => handleChange('password', e.target.value)} leftIcon={<LockIcon className="w-5 h-5" data-id="element-1591" />} rightIcon={<button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600" data-id="element-1592">
                  {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1593" /> : <EyeIcon className="w-5 h-5" data-id="element-1594" />}
                </button>} error={errors.password} helperText="Must be at least 8 characters" required data-id="element-1590" />

            <Input label="Confirm Password" type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={e => handleChange('confirmPassword', e.target.value)} leftIcon={<LockIcon className="w-5 h-5" data-id="element-1596" />} error={errors.confirmPassword} required data-id="element-1595" />

            <label className="flex items-start gap-2 cursor-pointer" data-id="element-1597">
              <input type="checkbox" className="w-4 h-4 text-primary-600 rounded mt-1" required data-id="element-1598" />
              <span className="text-sm text-gray-600" data-id="element-1599">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 hover:underline" data-id="element-1600">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-primary-600 hover:underline" data-id="element-1601">
                  Privacy Policy
                </Link>
              </span>
            </label>

            <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading} data-id="element-1602">
              Create Account
            </Button>
          </form>

          <div className="mt-6 text-center" data-id="element-1603">
            <p className="text-gray-600" data-id="element-1604">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium" data-id="element-1605">
                Sign In
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>;
}